import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Layout } from './components/Layout'

// Side-effect to prevent tree-shaking of react-router-dom components
const _routerComponents = { Routes, Route, Navigate, useNavigate, useLocation }
void _routerComponents
import { LoginPageAdmin } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { UsersPage } from './pages/UsersPage'
import { DronesPage } from './pages/DronesPage'
import { VehiclesPage } from './pages/VehiclesPage'
import { MissionsPage } from './pages/MissionsPage'
import { LicensesPage } from './pages/LicensesPage'
import { AkkusPage } from './pages/AkkusPage'
import { FlightsPage } from './pages/FlightsPage'
import { ExportPage } from './pages/ExportPage'
import { SettingsPage } from './pages/SettingsPage'
import { MedienPage } from './pages/MedienPage'
// Lazy load ChecklistenPage to use the code-split chunk
const ChecklistenPage = lazy(() => import('./pages/ChecklistenPage').then(m => ({ default: m.ChecklistenPage })))
// Lazy loaded - no need for tree-shake prevention
// Ensure component is not tree-shaken by referencing it
const _checklistenPage = ChecklistenPage
import { useAuth } from '@pilot-hub/auth'
import { ConnectionProvider, MissionProvider, FeedbackPage } from '@pilot-hub/ui'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (user) {
    // Default route based on pilotMode (stored in localStorage)
    const pilotMode = (localStorage.getItem('pilot-hub-pilot-mode') as 'PILOT' | 'CO_PILOT') || 'PILOT'
    const defaultPath = pilotMode === 'PILOT' ? '/flugbuch' : '/flug-erfassen'
    return <Navigate to={defaultPath} replace />
  }

  return <>{children}</>
}

// Force all page components into bundle by using them in a side-effect
// This component is never rendered but forces imports to be kept
function _ForcePageComponents() {
  return (
    <>
      <DashboardPage />
      <UsersPage />
      <LicensesPage />
      <VehiclesPage />
      <DronesPage />
      <AkkusPage />
      <MissionsPage />
      <FlightsPage />
      <ExportPage />
      <SettingsPage />
      <ChecklistenPage />
    </>
  )
}

function AppRoutes() {
  const router = useNavigate()
  // Expose router for LoginPage redirect without full reload
  ;(window as any).__PILOT_HUB_ROUTER__ = { navigate: router }
  // Reference force component to prevent tree-shaking
  void _ForcePageComponents
  // Prevent tree-shaking of react-router-dom components by using them in a side-effect
  const _routerComponents = { Routes, Route, Navigate, useNavigate, useLocation }
  void _routerComponents
  return (
    <ConnectionProvider staleMs={60000} ping>
    <MissionProvider>
      <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPageAdmin />
          </PublicRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="licenses" element={<LicensesPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="drones" element={<DronesPage />} />
        <Route path="akkus" element={<AkkusPage />} />
        <Route path="missions" element={<MissionsPage />} />
        <Route path="flights" element={<FlightsPage />} />
        <Route path="export" element={<ExportPage />} />
        <Route path="medien" element={<MedienPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="feedback" element={<FeedbackPage modul="admin" />} />
        <Route path="checklisten" element={
          <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" /></div>}>
            <ChecklistenPage />
          </Suspense>
        } />
        {/* Force inclusion: <ChecklistenPage /> */}
      </Route>
      </Routes>
    </MissionProvider>
    </ConnectionProvider>
  )
}

function App() {
  return (
    <div className="app min-h-screen bg-dark-bg">
      <AppRoutes />
    </div>
  )
}

export default App
